import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ListIcon from '@mui/icons-material/List';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AnomalyExceptionDialog from './AnomalyExceptionDialog';

// Generate mock daily rows from the source summary for placeholder mode
const generatePlaceholderData = (sourceSummary, startDate, endDate) => {
  const rows = [];
  const missingSet = new Set(sourceSummary.missingDays || []);
  const avg = sourceSummary.avgRowsPerDay || 100;
  const nullPct = sourceSummary.nullZeroPercent || 0;

  const d = new Date(startDate);
  const end = new Date(endDate);
  while (d <= end) {
    const dateStr = d.toISOString().split('T')[0];
    const isMissing = missingSet.has(dateStr);

    if (isMissing) {
      rows.push({
        businessDate: dateStr,
        storeId: 'ALL',
        rowCount: 0,
        nullZeroCount: 0,
        nullZeroPercent: 0,
        valueSum: 0,
      });
    } else {
      const jitter = 0.8 + Math.random() * 0.4; // 0.8–1.2x
      const rc = Math.round(avg * jitter);
      const nzCount = Math.round(rc * (nullPct / 100));
      rows.push({
        businessDate: dateStr,
        storeId: 'ALL',
        rowCount: rc,
        nullZeroCount: nzCount,
        nullZeroPercent: rc > 0 ? +((nzCount / rc) * 100).toFixed(1) : 0,
        valueSum: +(rc * (15 + Math.random() * 10)).toFixed(2),
      });
    }
    d.setDate(d.getDate() + 1);
  }
  return rows;
};

const fmtDate = (d) => {
  if (!d) return '';
  if (d instanceof Date) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  return String(d).split('T')[0];
};

/**
 * Format a numeric value according to the source's valueFormat.
 *   'dollars' → $1,234.56
 *   'hours'   → 1,234.5 hrs
 *   'number'  → 1,234
 */
const fmtValue = (val, format) => {
  if (val == null) return '-';
  if (format === 'hours') {
    return `${val.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} hrs`;
  }
  if (format === 'number') {
    return val.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  // default: dollars
  return `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const SourceDetail = ({
  sourceKey,
  sourceName,
  categoryKey,
  valueColumnName: valueColLabel,
  valueFormat = 'dollars',
  firebaseUser,
  startDate,
  endDate,
  selectedStore,
  isPlaceholder,
  sourceSummary,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('businessDate');
  const [sortDir, setSortDir] = useState('asc');
  const [showIssuesOnly, setShowIssuesOnly] = useState(true);
  const [exceptionRow, setExceptionRow] = useState(null);
  const [avgPopover, setAvgPopover] = useState({ anchorEl: null, row: null });

  // Display range boundaries — only rows within these dates are shown in the table
  const displayStart = fmtDate(startDate);
  const displayEnd = fmtDate(endDate);

  useEffect(() => {
    if (isPlaceholder && sourceSummary) {
      setData(generatePlaceholderData(sourceSummary, startDate, endDate));
    } else {
      fetchDetails();
    }
  }, [sourceKey, categoryKey, startDate, endDate, selectedStore, isPlaceholder]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);

      // Fetch 10 extra trailing days so the 4-day prior average has enough
      // context even when weekends or holidays create gaps
      const trailingStart = new Date(startDate);
      trailingStart.setDate(trailingStart.getDate() - 10);

      const params = new URLSearchParams({
        source: sourceKey,
        category: categoryKey,
        startDate: fmtDate(trailingStart),
        endDate: fmtDate(endDate),
        storeId: selectedStore,
      });

      const response = await axios.get(
        `/hd-api/data-integrity-2/details?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setData(response.data.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching DI2 details:', err);
      setError('Failed to load detail data.');
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  };

  // Rows within the selected date range (for display and issue counting)
  const displayData = useMemo(() => {
    return data.filter((row) => row.businessDate >= displayStart && row.businessDate <= displayEnd);
  }, [data, displayStart, displayEnd]);

  const sortedData = useMemo(() => {
    // Filter display data based on showIssuesOnly
    let filtered = displayData;
    if (showIssuesOnly) {
      filtered = displayData.filter(
        (row) => row.isMissing || row.isAnomaly || row.isDayOfWeekAnomaly || row.rowCount === 0
      );
    }

    // Sort the filtered data
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      const aVal = a[sortBy] ?? '';
      const bVal = b[sortBy] ?? '';

      let cmp = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal));
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [displayData, sortBy, sortDir, showIssuesOnly]);

  // Count issues for the button label (display range only)
  const issueCount = useMemo(() => {
    return displayData.filter(
      (row) => row.isMissing || row.isAnomaly || row.isDayOfWeekAnomaly || row.rowCount === 0
    ).length;
  }, [displayData]);

  // Build a per-store, per-date lookup so we can compute trailing 4-day
  // averages relative to each individual row's date.
  const storeDateLookup = useMemo(() => {
    const lookup = {};
    data.forEach((r) => {
      if (r.isMissing) return;
      const sid = String(r.storeId);
      if (!lookup[sid]) lookup[sid] = {};
      lookup[sid][r.businessDate] = { count: r.rowCount, value: r.valueSum };
    });
    return lookup;
  }, [data]);

  /**
   * For a given store + date, return the trailing 4-day entries (the 4 most
   * recent days WITH data before the row date), averages, and formatted
   * entries for the popover.
   */
  const getTrailing4d = (storeId, businessDate) => {
    const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const storeEntries = storeDateLookup[String(storeId)] || {};
    const rowDate = new Date(businessDate + 'T00:00:00');
    const entries = [];
    // Walk backwards from the day before the row date, collecting up to 4
    // days that actually have data (skips weekends / gaps automatically)
    for (let i = 1; entries.length < 4 && i <= 10; i++) {
      const d = new Date(rowDate);
      d.setDate(d.getDate() - i);
      const ds = fmtDate(d);
      if (storeEntries[ds]) {
        entries.push({ date: ds, dayName: DAY_NAMES[d.getUTCDay()], ...storeEntries[ds] });
      }
    }
    // Reverse so entries are in chronological order
    entries.reverse();
    const avgCount = entries.length > 0
      ? Math.round(entries.reduce((s, e) => s + e.count, 0) / entries.length)
      : null;
    const avgValue = entries.length > 0
      ? entries.reduce((s, e) => s + e.value, 0) / entries.length
      : null;
    return { entries, avgCount, avgValue };
  };

  const handleExport = async () => {
    try {
      const token = await firebaseUser.getIdToken(true);
      const params = new URLSearchParams({
        source: sourceKey,
        category: categoryKey,
        startDate: fmtDate(startDate),
        endDate: fmtDate(endDate),
        storeId: selectedStore,
      });

      const response = await axios.get(
        `/hd-api/data-integrity-2/details/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type: response.headers['content-type'],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `data-integrity-${sourceKey}-${categoryKey}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting DI2 details:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', backgroundColor: 'grey.50' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (displayData.length === 0) {
    return (
      <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          No detail data found for this source and date range.
        </Typography>
      </Box>
    );
  }

  const valLabel = valueColLabel || 'Value Sum';
  const columns = [
    { id: 'businessDate', label: 'Date' },
    { id: 'storeId', label: 'Store' },
    { id: 'rowCount', label: 'Row Count', numeric: true },
    { id: 'storeAvg4d', label: 'Store Avg (4d)', numeric: true },
    { id: 'valueSum', label: valLabel, numeric: true },
    { id: 'storeAvgValue4d', label: `Avg ${valLabel} (4d)`, numeric: true },
  ];

  // Compute average row count for highlighting (display range only)
  const avgRowCount =
    displayData.length > 0
      ? displayData.reduce((sum, r) => sum + r.rowCount, 0) / displayData.length
      : 0;
  const lowRowThreshold = avgRowCount * 0.5;

  return (
    <Box sx={{ p: '8px 16px 16px', backgroundColor: 'grey.50' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontFamily: 'Lato', fontWeight: 600 }}>
          {sourceName} — Daily Breakdown
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ButtonGroup size="small" variant="outlined">
            <Button
              onClick={() => setShowIssuesOnly(true)}
              variant={showIssuesOnly ? 'contained' : 'outlined'}
              color={showIssuesOnly ? 'warning' : 'inherit'}
              startIcon={<WarningAmberIcon />}
              sx={{ fontFamily: 'Lato', textTransform: 'none' }}
            >
              Issues ({issueCount})
            </Button>
            <Button
              onClick={() => setShowIssuesOnly(false)}
              variant={!showIssuesOnly ? 'contained' : 'outlined'}
              color={!showIssuesOnly ? 'primary' : 'inherit'}
              startIcon={<ListIcon />}
              sx={{ fontFamily: 'Lato', textTransform: 'none' }}
            >
              All Data
            </Button>
          </ButtonGroup>
          {!isPlaceholder && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              sx={{ fontFamily: 'Lato', textTransform: 'none', color: 'text.secondary' }}
            >
              Export
            </Button>
          )}
        </Box>
      </Box>
      <TableContainer
        sx={{
          maxHeight: 400,
          backgroundColor: 'white',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.numeric ? 'right' : 'left'}
                  sx={{ fontWeight: 600 }}
                >
                  <TableSortLabel
                    active={sortBy === col.id}
                    direction={sortBy === col.id ? sortDir : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, idx) => {
              const isMissing = row.isMissing || row.rowCount === 0;
              const isLowRow =
                !isMissing && row.rowCount < lowRowThreshold && lowRowThreshold > 0;
              const isAnomaly = row.isAnomaly;
              const isDayOfWeekAnomaly = row.isDayOfWeekAnomaly;
              const hasException = !!row.exception;
              const isIssueRow = isMissing || isAnomaly || isDayOfWeekAnomaly;

              let rowBg = undefined;
              if (hasException) {
                rowBg = 'rgba(46,125,50,0.05)';
              } else if (isMissing) {
                rowBg = 'rgba(198,40,40,0.08)';
              } else if (isAnomaly || isDayOfWeekAnomaly) {
                rowBg = 'rgba(230,81,0,0.08)';
              } else if (isLowRow) {
                rowBg = 'rgba(249,168,37,0.06)';
              }

              return (
                <TableRow
                  key={idx}
                  hover={isIssueRow || hasException}
                  onClick={isIssueRow || hasException ? () => setExceptionRow(row) : undefined}
                  sx={{
                    backgroundColor: rowBg,
                    cursor: isIssueRow || hasException ? 'pointer' : 'default',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {row.businessDate}
                      {hasException && (
                        <Chip
                          icon={<CheckCircleOutlineIcon />}
                          label="Exception"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{row.storeId}</TableCell>
                  <TableCell align="right">
                    {isMissing ? (
                      <span style={{ color: hasException ? '#757575' : '#c62828', fontWeight: 600 }}>
                        MISSING
                      </span>
                    ) : (isAnomaly || isDayOfWeekAnomaly) ? (
                      <span style={{ color: hasException ? '#757575' : '#e65100', fontWeight: 600 }}>
                        {row.rowCount.toLocaleString()}
                      </span>
                    ) : (
                      row.rowCount.toLocaleString()
                    )}
                  </TableCell>
                  {(() => {
                    const t4d = isMissing ? null : getTrailing4d(row.storeId, row.businessDate);
                    return (
                      <>
                        <TableCell
                          align="right"
                          onClick={!isMissing ? (e) => { e.stopPropagation(); setAvgPopover({ anchorEl: e.currentTarget, row }); } : undefined}
                          sx={!isMissing ? { cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } } : undefined}
                        >
                          {isMissing ? '-' : (t4d.avgCount ?? '-').toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          {isMissing ? (
                            <span style={{ color: hasException ? '#757575' : '#c62828', fontWeight: 600 }}>
                              MISSING
                            </span>
                          ) : (
                            fmtValue(row.valueSum, valueFormat)
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={!isMissing ? (e) => { e.stopPropagation(); setAvgPopover({ anchorEl: e.currentTarget, row }); } : undefined}
                          sx={!isMissing ? { cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } } : undefined}
                        >
                          {isMissing ? '-' : fmtValue(t4d.avgValue ?? 0, valueFormat)}
                        </TableCell>
                      </>
                    );
                  })()}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Popover
        open={!!avgPopover.anchorEl}
        anchorEl={avgPopover.anchorEl}
        onClose={() => setAvgPopover({ anchorEl: null, row: null })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {avgPopover.row && (() => {
          const t4d = getTrailing4d(avgPopover.row.storeId, avgPopover.row.businessDate);
          return (
            <Box sx={{ p: 2, minWidth: 260 }}>
              <Typography variant="subtitle2" sx={{ fontFamily: 'Lato', fontWeight: 600, mb: 1 }}>
                Store {avgPopover.row.storeId} — 4-Day Avg prior to {avgPopover.row.businessDate}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, py: 0.5 }}>Rows</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, py: 0.5 }}>{valLabel}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {t4d.entries.map((entry) => (
                    <TableRow key={entry.date}>
                      <TableCell sx={{ py: 0.5 }}>{entry.dayName} {entry.date}</TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>{entry.count.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>
                        {fmtValue(entry.value, valueFormat)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ py: 0.5, fontWeight: 600 }}>Average</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 600 }}>
                      {(t4d.avgCount ?? 0).toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 600 }}>
                      {fmtValue(t4d.avgValue ?? 0, valueFormat)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          );
        })()}
      </Popover>
      <AnomalyExceptionDialog
        open={!!exceptionRow}
        onClose={() => setExceptionRow(null)}
        row={exceptionRow}
        sourceKey={sourceKey}
        categoryKey={categoryKey}
        firebaseUser={firebaseUser}
        onSaved={() => fetchDetails()}
      />
    </Box>
  );
};

export default SourceDetail;
